import { createTZDate, type DateInput, getFacilityTimeZone } from './core';

/**
 * createFacilityDate
 *
 * Creates a TZDate object with the facility's timezone from the Jotai store.
 * If no date is provided, uses the current date and time.
 *
 * @param date - (Optional) The date to create a TZDate object from. Can be a Date object, ISO string, or timestamp. Defaults to the current date if not provided.
 * @returns A TZDate object configured with the facility's timezone from the timezoneAtom
 */
export const createFacilityDate = (date?: DateInput) => {
  return createTZDate(date, getFacilityTimeZone());
};

const defaultFormat = {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
} as Intl.DateTimeFormatOptions;

type FormatFacilityDateProps = {
  date?: DateInput;
  locale?: string;
  options?: Intl.DateTimeFormatOptions;
  withTimeZone?: boolean;
};

/**
 * formatFacilityDate
 *
 * Formats a date in the facility's timezone using Intl.DateTimeFormat.
 * Automatically applies the facility's timezone from the Jotai store and optionally includes the timezone name.
 * Merges provided format options with default options (month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit').
 *
 * @param date - (Optional) The date to format. Can be a Date object, ISO string, or timestamp. Defaults to the current date if not provided.
 * @param options - (Optional) Intl.DateTimeFormatOptions to customize the date format. Defaults to defaultFormat.
 * @param locale - (Optional) BCP 47 language tag for locale-specific formatting. Defaults to 'en-US'.
 * @param withTimeZone - (Optional) Whether to include the timezone name in the formatted output. Defaults to true.
 * @returns A formatted date string in the facility's timezone with the specified locale and format options
 */
export const formatFacilityDate = ({
  date,
  options = defaultFormat,
  locale = 'en-US',
  withTimeZone = true,
}: FormatFacilityDateProps) => {
  const facilityDate = createFacilityDate(date);

  // Make a copy of the options object as we will be modifying it below
  const formatOptions = { ...(options ?? defaultFormat) };
  formatOptions.timeZone = facilityDate.timeZone;
  // If the time zone name is not set, add it to the format options
  if (withTimeZone && !formatOptions.timeZoneName) {
    formatOptions.timeZoneName = 'short';
  }

  return facilityDate.toLocaleString(locale ?? 'en-US', formatOptions);
};

/**
 * formatFacilityTimezone
 *
 * Extracts and returns only the timezone name (e.g., 'PST', 'EST') for a given date in the facility's timezone.
 * Uses Intl.DateTimeFormat with formatToParts to isolate the timezone name component.
 *
 * @param date - (Optional) The date to get the timezone name for. Can be a Date object, ISO string, or timestamp. Defaults to the current date if not provided.
 * @param locale - (Optional) BCP 47 language tag for locale-specific timezone formatting. Defaults to 'en-US'.
 * @returns The short timezone name string (e.g., 'PST', 'EST') or an empty string if not found
 */
export const formatFacilityTimezone = ({
  date,
  locale = 'en-US',
}: Omit<FormatFacilityDateProps, 'options' | 'withTimeZone'>) => {
  const facilityDate = createFacilityDate(date);
  const formatter = new Intl.DateTimeFormat(locale ?? 'en-US', {
    timeZone: facilityDate.timeZone,
    timeZoneName: 'short',
  });
  const parts = formatter.formatToParts(facilityDate);
  const timeZonePart = parts.find((part) => part.type === 'timeZoneName');
  return timeZonePart?.value ?? '';
};

/**
 * formatFacilityDateAtTime
 *
 * Formats a date for display in table cells with a specific format: "MON DD, YY at H:MM AM/PM TZ".
 * Combines date and time formatting with uppercase month/day abbreviations and 12-hour time format.
 * Always includes the timezone name in the output.
 *
 * @param date - The date to format. Can be a Date object, ISO string, or timestamp.
 * @returns A formatted date string in the format "MON DD, YY at H:MM AM/PM TZ" (e.g., "NOV 17, 25 at 9:54 AM PST")
 */
export const formatFacilityDateAtTime = (date: DateInput) => {
  const dateObj = createFacilityDate(date);
  const dateOptions: Intl.DateTimeFormatOptions = {
    month: 'short', // Short month name (e.g., "Nov")
    day: 'numeric', // Day of the month (e.g., "17")
    year: '2-digit', // Year (e.g., "25")
    timeZone: dateObj.timeZone, // make sure to show in the correct timezone
  };
  const timeOptions: Intl.DateTimeFormatOptions = {
    hour: 'numeric', // Hour (e.g., "9")
    minute: '2-digit', // Minute (e.g., "05")
    hour12: true, // Use 12-hour format with AM/PM
    timeZone: dateObj.timeZone, // make sure to show in the correct timezone
    timeZoneName: 'short', // show the timezone name
  };
  return (
    new Intl.DateTimeFormat('en-US', dateOptions).format(dateObj).toUpperCase() +
    ' at ' +
    new Intl.DateTimeFormat('en-US', timeOptions).format(dateObj)
  );
};
